import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import responsiveStyles from './responsiveStyles.module.scss'

import FloatingInfoBox from '../../reusable/FloatingInfoBox'
import { ContactDetails, Person, Address, Review } from '../../formParts'
import { SaveValueInterface } from '../../../interfaces'
import { fieldValidation, uploadDataToServer } from '../../../helpers'
import { useUpdateClaimData } from '../../../claimProvider'
import { SignatureSentinel } from '../../formParts/signatureSentinel'
import NewsBanner from '../../newsBanner'
import EligibilitySection from '../../EligibilitySection'
import HeroHeading from '../../sections/HeroHeading'
import EligibilityFlow from '../../EligibilityFlow'
import { OpenBankingStep } from '../../formParts/openBanking'
import FAQ from '../../ui/FAQ'
import { militaryHearingLossFAQs } from '../../../data/militaryHearingLossFAQs'
import EligibilityChecklist from '../../sections/EligibilityChecklist'
import FloatingNavBar from '../../ui/FloatingNavBar'

const steps = [
    {
        text: 'Your Details',
        transitionPoint: 0,
        step: 1,
    },
    {
        text: 'Review & Sign',
        transitionPoint: 2,
        step: 2,
    },
    {
        text: 'Complete Application',
        transitionPoint: 4,
        step: 3,
    },
]

const LEAD_SOURCE = window.location.href
const LEAD_ORIGIN = 'webform_MBFINANCECHECK'
function ClaimFormMbFinanceCheck() {
    const [claimObject, setClaimObject] = useState<any>({
        leadOrigin: LEAD_ORIGIN,
        leadSource: LEAD_SOURCE,
    })
    const [groupsComplete, setGroupsComplete] = useState(0)
    const [activeGroup, setActiveGroup] = useState('address')
    const [displayedPercentage, setDisplayedPercentage] = useState(0)
    const [actualPercentage, setActualPercentage] = useState(0)
    const [showInfoBox, setShowInfoBox] = useState(true)
    // Once they're in the claim form, hide the sections below so the form is the only thing to do
    const [inClaim, setInClaim] = useState(false)
    // Once the eligibility check has started, the floating "Start a Claim" bar
    // would only send them back to the top of the form, so it's hidden
    const [checkStarted, setCheckStarted] = useState(false)

    // Dim the background grid and its lights once they're filling in the claim
    // form, so it distracts less (root.scss and GridOrbs fade on this class)
    useEffect(() => {
        document.body.classList.toggle('grid-quiet', inClaim)
        return () => document.body.classList.remove('grid-quiet')
    }, [inClaim])
    const updateClaim = useUpdateClaimData()

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => e.preventDefault()

    const saveFieldValue = (dataToSave: SaveValueInterface) => {
        console.log(dataToSave)
        let tempClaimObject = claimObject

        if (tempClaimObject) {
            tempClaimObject[dataToSave.field] = dataToSave.value
        }

        setClaimObject(tempClaimObject)
        // console.log("CLAIM OBJECT: ", claimObject);

        const validated = fieldValidation(dataToSave)

        if (validated.success) {
            return validated
        } else {
            return validated
        }
    }

    const activeGroupAssignment = async (group: string) => {
        console.log('next group to display: ', group)
        console.log('CLAIM OBEJECT : ', claimObject)

        let newGroupsComplete = groupsComplete

        const flowOrder = ['address', 'person', 'contact', 'review', 'signature', 'complete']
        const currentIndex = flowOrder.indexOf(activeGroup)
        const nextIndex = flowOrder.indexOf(group)

        if (nextIndex > currentIndex) {
            newGroupsComplete = nextIndex
        }

        if (activeGroup === 'address' && nextIndex > currentIndex) {
            setShowInfoBox(false)
        }

        setActiveGroup(group)

        if (group === 'review' || group === 'signature') {
            if (group === 'signature') {
                try {
                    const claimData = await uploadDataToServer(claimObject)
                    updateClaim({
                        ...claimData.claimData,
                        leadOrigin: LEAD_ORIGIN,
                        leadSource: LEAD_SOURCE,
                    })
                } catch (error) {
                    console.error('Error uploading data before signature:', error)
                }
            } else {
                updateClaim({
                    ...claimObject,
                    leadOrigin: LEAD_ORIGIN,
                    leadSource: LEAD_SOURCE,
                })
            }
        }

        window.scrollTo(0, 0)
        setGroupsComplete(newGroupsComplete)
    }

    useEffect(() => {
        // Calculate percentage based on the total number of steps
        // flowOrder has 6 steps, so each step is worth 100/5 = 20% (we subtract 1 because we start at 0%)
        const totalSteps = 5 // Total number of transitions in the flow
        const newPercentage = Math.min(Math.round((groupsComplete / totalSteps) * 100), 100)
        setActualPercentage(newPercentage)
    }, [groupsComplete])

    useEffect(() => {
        if (displayedPercentage === actualPercentage) return

        const isCountingUp = displayedPercentage < actualPercentage

        const animationInterval = setInterval(() => {
            setDisplayedPercentage((prev) => {
                if (isCountingUp) {
                    return prev < actualPercentage ? prev + 1 : actualPercentage
                } else {
                    return prev > actualPercentage ? prev - 1 : actualPercentage
                }
            })
        }, 20)

        return () => clearInterval(animationInterval)
    }, [actualPercentage, displayedPercentage])

    return (
        <div>
            <FloatingNavBar hidden={checkStarted} />
            <div className={`${styles.eligibilityContainer} ${responsiveStyles.eligibilityContainer}`}>
                <HeroHeading
                    subtitle="You served. We stand up for you."
                    fadeIn
                    // Still there in the claim form, just smaller, so the form sits higher
                    compact={inClaim}
                    tagline={
                        <>
                            Complete in <strong>45 seconds</strong>. No win, no fee.
                        </>
                    }
                />

                {/* Eligibility Flow */}
                <EligibilityFlow onClaimViewChange={setInClaim} onStartedChange={setCheckStarted} />
            </div>

            {!inClaim && (
                <>
                    {/* Eligibility Section */}
                    <EligibilitySection />

                    {/* Eligibility Checklist - Are you eligible? */}
                    <EligibilityChecklist />

                    {/* FAQ Section */}
                    <FAQ
                        title="Frequently Asked Questions About Military Hearing Loss Claims"
                        items={militaryHearingLossFAQs}
                    />
                </>
            )}

            {/* Original Form - Hidden for later */}
            <div className={`${styles.formContainer} ${responsiveStyles.formContainer}`} style={{ display: 'none' }}>
                <form className={`${styles.form}`} onSubmit={(e) => handleSubmit(e)}>
                    <div className={styles.formFields}>
                        <Address
                            saveValueFunct={saveFieldValue}
                            isActive={activeGroup}
                            assignGroup={activeGroupAssignment}
                        />
                        <Person
                            saveValueFunct={saveFieldValue}
                            isActive={activeGroup}
                            assignGroup={activeGroupAssignment}
                        />
                        <ContactDetails
                            saveValueFunct={saveFieldValue}
                            isActive={activeGroup}
                            assignGroup={activeGroupAssignment}
                        />
                        <Review
                            saveValueFunct={saveFieldValue}
                            isActive={activeGroup}
                            assignGroup={activeGroupAssignment}
                        />
                        <SignatureSentinel
                            saveValueFunct={saveFieldValue}
                            isActive={activeGroup}
                            assignGroup={activeGroupAssignment}
                        />
                        <OpenBankingStep
                            isActive={activeGroup}
                            saveValueFunct={saveFieldValue}
                            assignGroup={activeGroupAssignment}
                        />
                        {/* <Complete
                            isActive={activeGroup}
                            saveValueFunct={saveFieldValue}
                            assignGroup={activeGroupAssignment}
                        /> */}
                    </div>

                </form>
            </div>
        </div>
    )
}

export default ClaimFormMbFinanceCheck
